/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { Suspense } from 'react';
import { WidgetFallback } from '@mummssoftware/common-ui';
import { BrowserRouter } from 'react-router-dom';
import { Pim, auth } from '@mummssoftware/utils';
import { STRING_TRUE } from '@mummssoftware/utils/constants';
import { DEFAULT_LOCALE } from './i18n';
import Providers from './Providers';
import { PatientPortalContext, PatientPortalContextProvider } from './context';
import App from './containers/App';
import defaults from './translations/en.json';
const translations: NonNullable<mumms.JSONObject> = defaults;

let envHosts: Record<string, unknown> | undefined;
if (window.env.hosts) envHosts = window.env.hosts as Record<string, unknown>;

type PatientPortalProps = {
  /* whether the app is off or online */
  online: boolean;
  keycloak: Keycloak.KeycloakInstance;
  theme?: string | null; // todo make sure passing themes works when doing new runtime
  agency?: string | null;
  locale?: keyof mumms.translations;
  /*  controls whether certain elements (i.e. the top bar) display */
  embedded?: boolean;
  /* optional instance of pim, otherwise instantiates its own */
  pim?: Pim;
  /* if embedded title of the app or default Patient vitals title */
  appTitle?: string;
  /* pass currentUser if already retrieved to avoid excessive api calls */
  currentUser?: mumms.User;
  /* pass patientsList if already retrieved to avoid excessive api calls */
  patientsList?: any;
  /* if the component is embedded onClick to toggle PatientPortal to full-size */
  onClick?: () => void;
  /* dashboard */
  smallWidget?: boolean;
  /* dashboard key forcing React to render */
  key?: any;
  /* current user siteId - needed for agencies with multiple siteId */
  siteId?: string | null;
  /* If watermark should display on top of the widget. */
  /* If readonly = true, the widget will not be interactive */
  watermark?: {
    show: boolean;
    readOnly: boolean;
  };
  viewMode?: mumms.viewMode;
  printConfig?: mumms.printConfig;
  onWidgetReady?: (arg: mumms.ComponentName) => void;
};

interface PatientPortalState {
  context: PatientPortalContext | null;
}

const setError = (res: Response, type?: string) => ({
  appError: {
    status: res?.status || null,
    statusText: res?.statusText || null,
    type,
  },
});

const Content = React.memo(
  ({
    context,
    embedded,
    smallWidget,
  }: PatientPortalState & {
    embedded?: boolean;
    smallWidget?: boolean;
  }) => {
    if (context === null) {
      return (
        <WidgetFallback
          embedded={embedded}
          appTitle={translations.title as string}
          smallWidget={smallWidget}
        />
      );
    }
    return (
      <PatientPortalContextProvider value={context}>
        <Suspense
          fallback={
            <WidgetFallback
              embedded={embedded}
              appTitle={translations.title as string}
              smallWidget={smallWidget}
            />
          }
        >
          <App
            embedded={embedded}
            currentUser={context.currentUser}
            patientsList={context.patientsList}
            online={context.online}
            logout={context.keycloak && context.keycloak.logout}
            viewMode={context.viewMode}
            onWidgetReady={context.onWidgetReady}
            appTitle={context.appTitle}
            smallWidget={context.smallWidget}
            onClick={context.onClick}
          />
        </Suspense>
      </PatientPortalContextProvider>
    );
  },
);

Content.displayName = 'MainContent';

class PatientPortal extends React.PureComponent<
  PatientPortalProps,
  PatientPortalState
> {
  pim: Pim;

  agency?: string;

  constructor(props: PatientPortalProps) {
    super(props);

    const { keycloak, agency } = props;
    // avoid logic is agency is passed as props
    if (agency) {
      this.agency = agency;
    } else {
      // if the agency has not been passed as props and this could be an admin check if in url
      // if ADMIN agency in the URL
      const query = new URLSearchParams(window.location.search);
      const adminAgency = query.get('agency') as string;
      if (adminAgency) {
        this.agency = adminAgency;
      } else {
        // NON ADMIN look for agency in keycloack
        const parsedAgency = auth.getLdapDn(keycloak.idTokenParsed);
        this.agency = auth.getAgency(parsedAgency);
      }
    }
    const pimbaseurl = envHosts?.pim as string;
    const pgresturl = envHosts?.pimReader as string;
    this.pim =
      props.pim ||
      new Pim({
        url: pimbaseurl,
        keycloak,
        agency: this.agency,
        pgRestUrl: pgresturl,
      });

    this.state = {
      context: null,
    };
  }

  getInitialData = async () => {
    console.debug('patientPortalRuntime - getInitialData...');
    const currentUser = await this.pim.getUserInfo(
      this.agency || '',
      undefined,
      STRING_TRUE,
    );

    if (currentUser instanceof Response) {
      throw new Error(currentUser.statusText);
    }

    const { patientsList } = currentUser;

    console.debug(
      `patientPortalRuntime: patientsList: ${JSON.stringify(patientsList)}`,
    );
    console.debug(
      `patientPortalRuntime: currentUser: ${JSON.stringify(currentUser)}`,
    );

    let patientsListState: any;

    if (patientsList) {
      if (patientsList instanceof Response) {
        patientsListState = {
          ...setError(patientsList, 'patientsList'),
        };
      } else {
        patientsListState = { patientsList };
      }
    }

    this.setState({
      context: {
        keycloak: this.props.keycloak,
        currentUser,
        locale: this.props.locale || DEFAULT_LOCALE,
        agency: (this.props.agency || currentUser.currentHospiceBin) as string,
        ...patientsListState,
        appTitle: this.props.appTitle,
        smallWidget: this.props.smallWidget,
        watermark: this.props.watermark,
        viewMode: this.props.viewMode,
        onClick: this.props.onClick,
        onWidgetReady: this.props.onWidgetReady,
      },
    });
  };

  componentDidMount() {
    this.getInitialData();
  }

  render() {
    const { context } = this.state;
    const {
      embedded,
      theme,
      locale = DEFAULT_LOCALE,
      smallWidget,
    } = this.props;
    return (
      <Providers locale={locale} key={locale} theme={theme}>
        <BrowserRouter>
          <Content
            context={context}
            embedded={embedded}
            smallWidget={smallWidget}
          />
        </BrowserRouter>
      </Providers>
    );
  }
}

export default PatientPortal;
